import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, ArrowLeft, Mail, Instagram, Twitter, Linkedin, Play, ExternalLink, Camera, Video, Music, Image as ImageIcon, Cloud, Brain, Layers, Youtube, Link2, Puzzle, Square, Chrome, Home, Folder, User, Sun, Moon, Settings, Lock, Eye, EyeOff, X, ShieldCheck, Briefcase, Wrench, Palette } from 'lucide-react';

import { supabase } from './supabase';

import {
  DndContext,
  closestCenter,
  closestCorners,
  KeyboardSensor,
  MouseSensor,
  useSensor,
  useSensors,
  DragOverlay,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  rectSwappingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence } from 'motion/react';
import { ReactLenis } from 'lenis/react';
import { uiAudio } from './utils/audio';
import { CustomCursor } from './components/CustomCursor';
import { AboutContent, DEFAULT_ABOUT, mergeAboutContent } from './aboutContent';
import { Analytics } from "@vercel/analytics/react";

const cn = (...classes: (string | undefined | null | false)[]) => classes.filter(Boolean).join(' ');

export interface MenuBarItem {
  icon: React.ElementType;
  label: string;
}

interface MenuBarProps extends React.HTMLAttributes<HTMLDivElement> {
  items: MenuBarItem[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  className?: string;
}

const springConfig = {
  duration: 0.3,
  ease: "easeInOut"
};

export function MenuBar({ items, activeTab, onTabChange, className, ...props }: MenuBarProps) {
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = React.useState({ left: 0, width: 0 });
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (activeIndex !== null && menuRef.current && tooltipRef.current) {
      const menuItem = menuRef.current.children[activeIndex] as HTMLElement;
      const menuRect = menuRef.current.getBoundingClientRect();
      const itemRect = menuItem.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      const left = itemRect.left - menuRect.left + (itemRect.width - tooltipRect.width) / 2;

      setTooltipPosition({
        left: Math.max(0, Math.min(left, menuRect.width - tooltipRect.width)),
        width: tooltipRect.width
      });
    }
  }, [activeIndex]);

  return (
    <div className={cn(className)} {...props}>
      <AnimatePresence>
        {activeIndex !== null && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={springConfig}
            className="absolute left-0 right-0 -bottom-[35px] pointer-events-none z-50"
          >
            <motion.div
              ref={tooltipRef}
              className={cn(
                "h-7 px-3 rounded-lg inline-flex justify-center items-center overflow-hidden",
                "bg-[#1A1A1A]/95 backdrop-blur text-white",
                "border border-white/10",
                "shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
              )}
              initial={{ x: tooltipPosition.left }}
              animate={{ x: tooltipPosition.left }}
              transition={springConfig}
              style={{ width: "auto" }}
            >
              <p className="text-[13px] font-medium leading-tight whitespace-nowrap">
                {items[activeIndex].label}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        ref={menuRef}
        className={cn(
          "h-12 px-1.5 inline-flex justify-center items-center gap-[3px] overflow-hidden z-10",
          "rounded-full bg-[#1A1A1A]/80 backdrop-blur-md text-white",
          "border border-white/10",
          "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_8px_16px_-4px_rgba(0,0,0,0.1)]"
        )}
      >
        {items.map((item, index) => {
          const isActive = activeTab === item.label || (activeTab.startsWith('Project:') && item.label === 'Home');
          return (
            <button
              key={index}
              onClick={() => onTabChange(item.label)}
              className={cn(
                "w-9 h-9 rounded-full flex justify-center items-center gap-2 transition-all duration-200 relative",
                isActive ? "bg-white/25 shadow-[0_0_8px_rgba(255,255,255,0.15)]" : "hover:bg-white/10"
              )}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {isActive && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-white rounded-full" />
              )}
              <div className="flex justify-center items-center">
                <div className="w-[18px] h-[18px] flex justify-center items-center overflow-hidden">
                  <item.icon className="w-full h-full" />
                </div>
              </div>
              <span className="sr-only">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

type GridItemType = {
  id: string;
  type: string;
  rowSpan?: number;
  colSpan?: number;
  size?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  page?: string;
  image?: string;
  video?: string;
  content?: string;
  link?: string;
  password?: string;
  templateLogo?: string;
  templateHeaderPill?: string;
  templateHeading?: string;
  templateSubHeading?: string;
  templateBody?: string;
  templateResponsibilities?: string;
  templateImages?: string[];
  templateLayoutImages?: { id: string, url: string, size: 'large' | 'small', hasSubcollectionData?: boolean }[];
  templateLink?: string;
  caseStudyLink?: string;
  isExternalOnly?: boolean;
};

// Password Modal Component
const PasswordModal = ({ isOpen, onClose, onSubmit, projectTitle }: { isOpen: boolean, onClose: () => void, onSubmit: (password: string) => void, projectTitle: string }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isShaking, setIsShaking] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }
    onSubmit(password);
  };

  // Allow parent to trigger error state
  React.useEffect(() => {
    if (isOpen) {
      setPassword('');
      setError('');
      setShowPassword(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className={`relative w-full max-w-[400px] bg-[#1A1A1A] border border-white/10 rounded-[24px] p-8 shadow-2xl ${isShaking ? 'animate-shake' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all duration-200"
            >
              <X size={16} />
            </button>

            {/* Lock Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                <Lock size={28} className="text-white/70" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6">
              <h2 className="text-white text-xl font-semibold mb-1">Protected Project</h2>
              <p className="text-gray-400 text-sm">Enter the password to view <span className="text-white/80 font-medium">{projectTitle}</span></p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="Enter password"
                  autoFocus
                  className={`w-full bg-[#121111] border ${error ? 'border-red-500/50' : 'border-white/10 focus:border-white/30'
                    } rounded-xl p-4 pr-12 text-white placeholder:text-gray-500 focus:outline-none transition-colors text-sm`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors p-1"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-400 text-xs px-1"
                >
                  {error}
                </motion.p>
              )}

              <button
                type="submit"
                className="w-full bg-white text-black font-medium py-3.5 rounded-xl hover:bg-gray-100 transition-colors text-sm flex items-center justify-center gap-2"
              >
                <ShieldCheck size={16} />
                Unlock Project
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const INITIAL_ITEMS: GridItemType[] = [
  { id: 'bio', type: 'bio', rowSpan: 1 },
  { id: 'ninja', type: 'project', rowSpan: 2, title: 'Ninja', subtitle: 'E-commerce', description: 'Saiss AI is a Saudi company specializes in AI', image: '/ninja.png' },
  { id: 'email', type: 'email', rowSpan: 1 },
  { id: 'theme-toggle', type: 'theme-toggle', rowSpan: 1 },
  { id: 'sciliamia', type: 'project', rowSpan: 2, title: 'Sciliamia', subtitle: 'B2C, Design System', description: 'E-commerce app for groceries in Kuawait', image: '/Untitled design (19).png' },
  { id: 'befitgo', type: 'project', rowSpan: 2, title: 'Beft Go', subtitle: 'SaaS, E-commerce', description: 'A kinaesthetic coffee experience.', image: 'https://picsum.photos/seed/befitgo/800/1000' },
  { id: 'oldportfolio', type: 'project', rowSpan: 2, title: 'Old Portfolio', subtitle: 'Web Development', image: '/oldportfolio.png' },
  { id: 'nexus', type: 'project', rowSpan: 2, title: 'Nexus', subtitle: 'Mobile App', description: 'Next-gen social platform.', image: 'https://picsum.photos/seed/nexus/800/1000' },
  { id: 'variant1', type: 'variant1', rowSpan: 2 },
  { id: 'basta', type: 'project', rowSpan: 2, title: 'Basta', subtitle: 'E-commerce, Design System', description: 'E-commerce app for groceries in Kuawait', image: '/basta.png' },
  { id: 'live-projects', type: 'live-projects', rowSpan: 2, colSpan: 2 },
  { id: 'saissai', type: 'project', rowSpan: 2, title: 'Saiss AI', subtitle: 'Web Design', description: 'Saiss AI is a Saudi company specializes in AI', image: '/saiss logo.png' },
  { id: 'mysource', type: 'project', rowSpan: 2, title: 'My Source Network', subtitle: 'B2C, Design System', description: 'E-commerce app for groceries in Kuawait', image: '/mysource.png' },
  { id: 'about', type: 'about', rowSpan: 2 },
  { id: 'clients', type: 'clients', rowSpan: 2, colSpan: 2 },
];

const ABOUT_ITEMS: GridItemType[] = [
  { id: 'about-bio', type: 'about-bio', rowSpan: 1, colSpan: 2 },
  { id: 'about-experience', type: 'about-experience', rowSpan: 2, colSpan: 1 },
  { id: 'about-pictures', type: 'variant1', rowSpan: 2, colSpan: 1 },
  { id: 'about-education', type: 'about-education', rowSpan: 1, colSpan: 1 },
  { id: 'about-skills', type: 'about-skills', rowSpan: 1, colSpan: 1 },
  { id: 'about-courses', type: 'about-courses', rowSpan: 1, colSpan: 1 },
  { id: 'about-recognitions', type: 'about-recognitions', rowSpan: 1, colSpan: 1 },
  { id: 'about-email', type: 'about-email', rowSpan: 1, colSpan: 2 },
];

// Shiny Glint Effect Component (lightweight CSS-only)
const ShinyGlint = () => null;

const AboutCard = ({ setCurrentPage, image }: { setCurrentPage: (page: string) => void, image?: string }) => {
  return (
    <motion.div
      className="bg-[#121111] rounded-[32px] relative overflow-hidden group h-full w-full transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-[1.02] dark:hover:shadow-2xl hover:z-10 cursor-pointer"
      onClick={() => setCurrentPage('About')}
      whileHover={{ y: -5 }}
    >
      <ShinyGlint />
      {/* Bottom Content (Revealed on hover) */}
      <div className="absolute bottom-8 md:bottom-12 left-6 md:left-8 right-6 md:right-8 flex flex-col gap-4 opacity-0 translate-y-8 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] delay-75 z-0">
        <div className="flex flex-col gap-2">
          <h2 className="text-white text-[32px] md:text-[42.7px] font-medium leading-none">About Me</h2>
          <p className="text-[#8A8A8A] text-[20px] md:text-[24px] leading-tight transition-colors group-hover:text-white/60">Get to know the designer</p>
        </div>
      </div>

      {/* Image */}
      <motion.img
        src={image || `https://picsum.photos/seed/about/800/1000`}
        alt="About Me"
        className="absolute top-0 left-0 w-full h-full object-cover rounded-[32px] transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:top-4 md:group-hover:top-5 group-hover:left-4 md:group-hover:left-5 group-hover:w-[calc(100%-32px)] md:group-hover:w-[calc(100%-40px)] group-hover:h-[52%] group-hover:rounded-[24px] pointer-events-none z-10"
      />

      {/* Floating Logos */}
      <div className="absolute top-8 left-8 z-20 flex flex-col gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-700 delay-150">
        {[
          { src: '/figma logo.webp', delay: 0 },
          { src: '/Framer logo.avif', delay: 0.2 },
          { src: '/react-logo-rounded-free-png.webp', delay: 0.4 }
        ].map((logo, i) => (
          <motion.img
            key={i}
            src={logo.src}
            alt="Tool logo"
            className="w-10 h-10 md:w-12 md:h-12 object-contain drop-shadow-xl filter grayscale group-hover:grayscale-0 transition-all duration-500 cursor-default"
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: logo.delay }}
            whileHover={{ scale: 1.2, rotate: 10 }}
          />
        ))}
      </div>

      {/* Top Right Button */}
      <div className="absolute top-6 right-6 md:top-8 md:right-8 z-20 flex items-center gap-3">
        {/* Hover Label */}
        <div className="bg-[#171717]/30 backdrop-blur-md rounded-[80px] px-4 py-2 h-12 flex items-center justify-center opacity-0 translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]">
          <span className="text-white text-[14px] font-medium whitespace-nowrap">Read More</span>
        </div>
        {/* Icon */}
        <motion.div
          whileHover={{ scale: 1.1, rotate: 10 }}
          className="w-12 h-12 bg-[#171717]/30 backdrop-blur-md rounded-full flex items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:bg-white text-white group-hover:text-black shadow-lg"
        >
          <User size={20} />
        </motion.div>
      </div>
    </motion.div>
  );
}

const TopLoadingBar = ({ isVisible }: { isVisible: boolean }) => (
  <AnimatePresence>
    {isVisible && (
      <motion.div
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1.5, ease: "easeInOut" }}
        className="fixed top-0 left-0 right-0 h-1 bg-white dark:bg-white z-[1000] origin-left shadow-[0_0_10px_rgba(255,255,255,0.5)]"
      />
    )}
  </AnimatePresence>
);

const ProjectCard = React.memo(({ id, title, subtitle, description, image, video, link, size, password, isExternalOnly, setCurrentPage, onPasswordRequest, onPreload }: { id?: string, title: string, subtitle: string, description?: string, image: string, video?: string, link?: string, size?: string, password?: string, isExternalOnly?: boolean, setCurrentPage?: (page: string) => void, onPasswordRequest?: (projectId: string, projectTitle: string) => void, onPreload?: (id: string, navigate: boolean) => void }) => {
  const hasPassword = !!password;

  const content = (
    <motion.div
      className="bg-[#121111] rounded-[32px] relative overflow-hidden group h-full w-full transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-[1.02] dark:hover:shadow-2xl hover:z-10"
      whileHover={{ y: -5 }}
    >
      <ShinyGlint />
      {/* Bottom Content (Revealed on hover) */}
      <div className="absolute bottom-8 md:bottom-12 left-6 md:left-8 right-6 md:right-8 flex flex-col gap-4 opacity-0 translate-y-8 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] delay-75 z-0">
        <div className="flex flex-col gap-2">
          <h2 className="text-white text-[32px] md:text-[42.7px] font-medium leading-none">{title}</h2>
          <p className="text-[#8A8A8A] text-[20px] md:text-[24px] leading-tight group-hover:text-white/60 transition-colors">{subtitle}</p>
        </div>
        {description && (
          <p className="text-white/80 text-[18px] md:text-[24px] leading-tight max-w-[90%]">{description}</p>
        )}
      </div>

      {/* Cover (video takes priority, image is used as its poster) */}
      {video ? (
        <video
          src={video}
          poster={image || undefined}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          className="absolute top-0 left-0 w-full h-full object-cover rounded-[32px] transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:top-4 md:group-hover:top-5 group-hover:left-4 md:group-hover:left-5 group-hover:w-[calc(100%-32px)] md:group-hover:w-[calc(100%-40px)] group-hover:h-[52%] group-hover:rounded-[24px] pointer-events-none z-10"
        />
      ) : (
        <motion.img
          src={image}
          alt={title}
          loading="eager"
          decoding="async"
          className="absolute top-0 left-0 w-full h-full object-cover rounded-[32px] transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:top-4 md:group-hover:top-5 group-hover:left-4 md:group-hover:left-5 group-hover:w-[calc(100%-32px)] md:group-hover:w-[calc(100%-40px)] group-hover:h-[52%] group-hover:rounded-[24px] pointer-events-none z-10"
        />
      )}

      {/* Top Right Button */}
      <div className="absolute top-6 right-6 md:top-8 md:right-8 z-20 flex items-center gap-3">
        {/* Hover Label */}
        <div className="bg-[#171717]/30 backdrop-blur-md rounded-[80px] px-4 py-2 h-12 flex items-center justify-center opacity-0 translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]">
          <span className="text-white text-[14px] font-medium whitespace-nowrap">{hasPassword ? 'Protected' : isExternalOnly ? 'Visit Link' : 'View Project'}</span>
        </div>
        {/* Icon */}
        <motion.div
          whileHover={{ scale: 1.1, rotate: -45 }}
          className="w-12 h-12 bg-[#171717]/30 backdrop-blur-md rounded-full flex items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:bg-white text-white group-hover:text-black shadow-lg"
        >
          {hasPassword ? <Lock size={20} /> : <ArrowRight size={20} className="-rotate-45" />}
        </motion.div>
      </div>
    </motion.div>
  );

  return (
    <div
      className="block w-full h-full cursor-pointer cursor-view-project"
      onMouseEnter={() => id && onPreload && onPreload(id, false)}
      onClick={() => {
        if (!id) return;
        if (isExternalOnly && link) {
          window.open(link, '_blank', 'noopener,noreferrer');
          return;
        }
        if (hasPassword && onPasswordRequest) {
          onPasswordRequest(id, title);
        } else if (onPreload) {
          onPreload(id, true);
        } else if (setCurrentPage) {
          setCurrentPage(`Project:${id}`);
        }
      }}
    >
      {content}
    </div>
  );
});

const SocialIcon = ({ icon }: { icon: React.ReactNode }) => (
  <div className="w-14 h-14 md:w-16 md:h-16 bg-black dark:bg-white rounded-full flex items-center justify-center text-white dark:text-black hover:scale-110 transition-all duration-500 cursor-pointer">
    {icon}
  </div>
);

const ThemeToggleCard = ({ isDarkMode, toggleTheme }: { isDarkMode: boolean, toggleTheme: () => void }) => {
  return (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-[24px] h-full relative flex flex-col items-center justify-center transition-all duration-500">
      <div className="absolute top-6 bg-[#F3F2F2] dark:bg-[#2A2A2A] rounded-full px-6 py-2.5 text-[14.2px] font-medium transition-colors duration-500">
        Theme
      </div>
      <button
      aria-label={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
        onClick={toggleTheme}
        onPointerDown={(e) => e.stopPropagation()}
        className="mt-8 w-[120px] h-[60px] bg-[#F3F2F2] dark:bg-[#2A2A2A] rounded-full p-2 flex items-center relative transition-colors duration-500 cursor-pointer"
      >
        <div className={`w-[44px] h-[44px] bg-white dark:bg-[#121111] rounded-full flex items-center justify-center transition-transform duration-500 z-10 ${isDarkMode ? 'translate-x-[60px]' : 'translate-x-0'}`}>
          {isDarkMode ? <Moon size={20} className="text-white" /> : <Sun size={20} className="text-[#171717]" />}
        </div>
        <div className="absolute inset-0 flex justify-between items-center px-5 pointer-events-none">
          <Sun size={20} className={`text-[#8A8A8A] transition-opacity duration-500 ${isDarkMode ? 'opacity-100' : 'opacity-0'}`} />
          <Moon size={20} className={`text-[#8A8A8A] transition-opacity duration-500 ${isDarkMode ? 'opacity-0' : 'opacity-100'}`} />
        </div>
      </button>
    </div>
  );
};

const VariantCard = ({ images: propImages }: { images?: string[] }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const defaultImages = [
    "https://picsum.photos/seed/variant1_1/800/1000",
    "https://picsum.photos/seed/variant1_2/800/1000",
    "https://picsum.photos/seed/variant1_3/800/1000",
    "https://picsum.photos/seed/variant1_4/800/1000",
  ];

  const images = propImages && propImages.length > 0 ? propImages : defaultImages;

  const icons = [
    { src: "/car-profile.svg", alt: "Car" },
    { src: "/paw-print.svg", alt: "Paw" },
    { src: "/road-horizon.svg", alt: "Road" },
    { src: "/sun-horizon.svg", alt: "Sun" },
  ];

  return (
    <div className="bg-[#121111] rounded-[32px] h-full relative overflow-hidden group">
      {images.map((src, index) => (
        <img
          key={src}
          src={src}
          alt={`Variant ${index + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 pointer-events-none ${activeIndex === index ? 'opacity-100' : 'opacity-0'}`}
        />
      ))}

      {/* Navigation Bar */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-row items-center gap-2 p-1.5 bg-white/30 border border-white/40 rounded-full backdrop-blur-md z-20 translate-y-24 group-hover:translate-y-0 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
        onPointerDown={(e) => e.stopPropagation()} // Prevent drag when interacting with nav
      >
        {icons.map((icon, index) => (
          <button
            key={index}
            onClick={() => setActiveIndex(index)}
            className={`relative z-10 w-10 h-10 flex items-center justify-center transition-colors duration-300 cursor-pointer rounded-full ${activeIndex === index ? 'text-[#171717]' : 'text-white hover:text-white/80'
              }`}
            title={icon.alt}
          >
            {activeIndex === index && (
              <motion.div
                layoutId="activeVariantPill"
                className="absolute inset-0 bg-white rounded-full -z-10"
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
            )}
            <div
              className="w-[22px] h-[22px]"
              style={{
                WebkitMaskImage: `url('${icon.src}')`,
                maskImage: `url('${icon.src}')`,
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                backgroundColor: 'currentColor',
              }}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const LiveProjectsCard = ({ isDarkMode, projects }: { isDarkMode: boolean, projects?: { image: string, link: string }[] }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const defaultCards = [
    { hoverColor: '#1CCBD2', img: 'https://picsum.photos/seed/live1/300/400', link: '' },
    { hoverColor: '#5A2967', img: 'https://picsum.photos/seed/live2/300/400', link: '' },
    { hoverColor: '#1C1C1C', img: 'https://picsum.photos/seed/live3/300/400', link: '' },
    { hoverColor: '#056E26', img: 'https://picsum.photos/seed/live4/300/400', link: '' },
    { hoverColor: '#E5E7EB', img: 'https://picsum.photos/seed/live5/300/400', link: '' },
  ];

  const sourceItems = projects && projects.length > 0 ? projects.map(p => ({ img: p.image, link: p.link })) : defaultCards;
  const N = sourceItems.length;

  const cards = sourceItems.map((p, i) => {
    const offset = i - (N - 1) / 2;
    return {
      x: offset * 200,
      r: offset * 3,
      hoverColor: defaultCards[i % defaultCards.length].hoverColor,
      img: p.img,
      z: 50 - Math.abs(offset) * 10,
      link: p.link
    };
  });

  return (
    <div
      className="bg-white dark:bg-[#1A1A1A] rounded-[24px] h-full w-full relative overflow-hidden flex items-center justify-center border border-gray-200 dark:border-white/10 transition-colors duration-500 ease-out"
    >
      <div className="absolute top-8 left-1/2 -translate-x-1/2 bg-[#F3F2F2] dark:bg-[#2A2A2A] rounded-full px-6 py-2.5 text-[14.2px] font-medium flex items-center gap-3 z-40 shadow-sm transition-colors duration-500">
        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
        Live Projects
      </div>

      {/* Rotated Cards */}
      <div className="relative w-full h-full flex items-center justify-center scale-[0.4] sm:scale-75 md:scale-100">
        {cards.map((card, index) => {
          const isHovered = hoveredIndex === index;

          const cardContent = (
            <>
              {card.img ? (
                <img src={card.img} className="w-full h-full object-cover rounded-[20px] pointer-events-none" alt={`Live Project ${index + 1}`} />
              ) : (
                <div className="w-full h-full bg-[#333] rounded-[20px] flex items-center justify-center text-sm text-gray-500">No Image</div>
              )}
            </>
          );

          const className = `absolute w-[290px] h-[412px] bg-transparent rounded-[20px] shadow-2xl flex items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] cursor-pointer`;
          const style = {
            top: '50%',
            left: '50%',
            zIndex: isHovered ? 50 : card.z,
          };

          return (
            <motion.div
              key={index}
              className={className}
              style={style}
              initial={false}
              animate={{
                x: `-50%`,
                y: isHovered ? `-60%` : `-50%`,
                translateX: card.x,
                rotate: isHovered ? 0 : card.r,
                scale: isHovered ? 1.15 : 1,
              }}
              transition={{ type: "spring", stiffness: 300, damping: 25, mass: 0.5 }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => card.link && window.open(card.link, '_blank', 'noopener,noreferrer')}
            >
              {cardContent}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

const ClientsCard = () => {
  const clients = [
    { name: "Datalink", src: "/datalink-logo.svg" },
    { name: "Menzies", src: "/Menzies_Logo-FINAL-03-e1665553137571.png" },
    { name: "Siciliamia", src: "/MainLogoSM.avif" },
    { name: "Ninja", src: "/ninja.png" },
    { name: "Bank of Khartoum", src: "/bank-of-khartoum_sd.png" },
    { name: "KSGAAL", src: "/King Salman Global Academy for Arabic Language -01.png" },
    { name: "Ebinaa", src: "/ebinaalogo.png" },
    { name: "Mbok", src: "/Mbok logo.png" },
    { name: "Saiss", src: "/saiss logo.png" },
  ];

  return (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-[40px] h-full w-full relative overflow-hidden flex flex-col items-center pt-5 pb-8 min-h-[400px] transition-colors duration-500 border border-gray-200 dark:border-white/10">
      {/* Title Pill */}
      <div className="bg-[#F3F2F2] dark:bg-[#2A2A2A] rounded-full w-[235px] h-[50px] flex items-center justify-center shrink-0 mb-8 transition-colors duration-500">
        <span className="font-medium text-[14.2px] text-[#171717] dark:text-white transition-colors duration-500">Current and Previous Clients</span>
      </div>

      {/* Clients Grid */}
      <div className="w-full flex-1 px-4 md:px-10 flex flex-col justify-center">
        <div className="grid grid-cols-2 sm:grid-cols-3 w-full gap-y-6 md:gap-y-10 gap-x-4 md:gap-x-6 items-center place-items-center">
          {clients.map((client, i) => (
            <div key={i} className="flex items-center justify-center cursor-pointer overflow-hidden p-2 w-full">
              <img
                src={client.src}
                alt={client.name}
                width={170}
                height={100}
                loading="lazy"
                className="w-full h-auto max-w-[120px] sm:max-w-[140px] md:max-w-[170px] max-h-[70px] sm:max-h-[80px] md:max-h-[100px] object-contain filter grayscale opacity-60 dark:opacity-80 dark:brightness-200 hover:grayscale-0 hover:opacity-100 hover:brightness-100 transition-all duration-300"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AboutEmailCard = () => {
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent("New message from your portfolio");
    const body = encodeURIComponent(`From: ${email}\n\nMessage:\n${message}`);
    window.location.href = `mailto:munzer3a6i@gmail.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-[24px] p-8 h-full flex flex-col justify-center gap-4 transition-all duration-500">
      <h3 className="text-xl font-bold">Get in touch</h3>
      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          required
          className="bg-gray-100 dark:bg-[#2A2A2A] rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-black dark:focus:ring-white transition-all"
        />
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Your message"
          required
          rows={3}
          className="bg-gray-100 dark:bg-[#2A2A2A] rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-black dark:focus:ring-white transition-all resize-none"
        />
        <button type="submit" className="bg-black dark:bg-white text-white dark:text-black px-6 py-3 rounded-xl font-medium hover:scale-105 transition-transform self-start">
          Send Message
        </button>
      </form>
    </div>
  );
};

const AboutContentContext = React.createContext<AboutContent>(DEFAULT_ABOUT);

const ABOUT_LOGOS = [
  { src: '/figma logo.webp' },
  { src: '/Framer logo.avif' },
  { src: '/react-logo-rounded-free-png.webp' }
];

const AboutBioCard = () => {
  const about = React.useContext(AboutContentContext);
  return (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-[24px] p-8 md:p-12 h-full flex flex-col justify-center gap-6 transition-all duration-500 relative overflow-hidden group border border-gray-200 dark:border-white/10">
      <h2 className="text-2xl md:text-[28px] font-bold relative z-10 text-[#171717] dark:text-white">{about.name}</h2>
      <p className="text-[16px] md:text-[18px] leading-[26px] md:leading-[30px] font-medium text-[#666666] dark:text-[#A3A3A3] whitespace-pre-wrap relative z-10">
        {about.content}
      </p>

      {/* Background Animated Logos */}
      <div className="absolute -right-4 -bottom-4 opacity-[0.03] dark:opacity-[0.05] group-hover:opacity-[0.15] dark:group-hover:opacity-[0.2] transition-opacity duration-700 flex gap-6 z-0 pointer-events-none">
        {ABOUT_LOGOS.map((logo, i) => (
          <motion.img
            key={i}
            src={logo.src}
            className="w-32 h-32 md:w-48 md:h-48 object-contain filter grayscale"
            animate={{ y: [0, -15, 0], rotate: [0, 5, -5, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
          />
        ))}
      </div>
    </div>
  );
};

const AboutExperienceCard = () => {
  const about = React.useContext(AboutContentContext);
  return (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-[24px] p-8 md:p-10 h-full flex flex-col gap-6 transition-all duration-500 overflow-hidden border border-gray-200 dark:border-white/10">
      <h3 className="text-xl md:text-[24px] font-bold text-[#171717] dark:text-white shrink-0">Experience</h3>
      <div
        className="flex flex-col gap-6 overflow-y-auto pr-2 touch-pan-y cursor-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-black/10 dark:[&::-webkit-scrollbar-thumb]:bg-white/10 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-black/20 dark:hover:[&::-webkit-scrollbar-thumb]:bg-white/20"
        onPointerDown={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {about.experience.map((job, i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <h4 className="font-semibold text-[15px] md:text-[16px] text-[#171717] dark:text-white leading-tight">{job.role}</h4>
            <p className="text-[13px] md:text-[14px] text-[#171717]/60 dark:text-white/60 font-medium">{[job.company, job.period].filter(Boolean).join(' • ')}</p>
            {job.description && (
              <p className="text-[13px] md:text-[14px] text-[#666666] dark:text-[#A3A3A3] leading-[20px] md:leading-[22px] whitespace-pre-wrap">
                {job.description}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const AboutEducationCard = () => {
  const about = React.useContext(AboutContentContext);
  return (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-[24px] p-8 md:p-10 h-full flex flex-col justify-center gap-6 transition-all duration-500 border border-gray-200 dark:border-white/10">
      <h3 className="text-xl md:text-[24px] font-bold text-[#171717] dark:text-white">Education</h3>
      {about.education.map((edu, i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <h4 className="font-semibold text-[16px] md:text-[18px] text-[#171717] dark:text-white">{edu.school}</h4>
          <p className="text-[14px] md:text-[15px] text-[#666666] dark:text-[#A3A3A3] font-medium">{edu.degree}</p>
        </div>
      ))}
    </div>
  );
};

const AboutSkillsCard = () => {
  const about = React.useContext(AboutContentContext);
  return (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-[24px] p-8 md:p-10 h-full flex flex-col justify-center gap-6 transition-all duration-500 group relative overflow-hidden border border-gray-200 dark:border-white/10">
      <div className="relative z-10 flex flex-col gap-5">
        <h3 className="text-xl md:text-[24px] font-bold text-[#171717] dark:text-white">Skills & Tools</h3>
        <div className="flex flex-col gap-4">
          {about.skills.map((group, i) => (
            <div key={i} className="flex flex-col gap-1">
              <span className="font-semibold text-[15px] md:text-[16px] text-[#171717] dark:text-white">{group.category}</span>
              <p className="text-[14px] md:text-[15px] text-[#666666] dark:text-[#A3A3A3] font-medium leading-[22px]">{group.items}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-4 mt-2">
          {ABOUT_LOGOS.map((logo, i) => (
            <motion.img
              key={i}
              src={logo.src}
              alt="tool logo"
              className="w-10 h-10 md:w-12 md:h-12 object-contain"
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.15 }}
              whileHover={{ scale: 1.15, rotate: 10 }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const AboutCoursesCard = () => {
  const about = React.useContext(AboutContentContext);
  return (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-[24px] p-8 md:p-10 h-full flex flex-col justify-center gap-6 transition-all duration-500 border border-gray-200 dark:border-white/10">
      <h3 className="text-xl md:text-[24px] font-bold text-[#171717] dark:text-white">Courses</h3>
      <ul className="text-[14px] md:text-[15px] font-medium flex flex-col gap-3 text-[#666666] dark:text-[#A3A3A3]">
        {about.courses.map((course, i) => (
          <li key={i} className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#171717] dark:bg-white shrink-0" />{course}</li>
        ))}
      </ul>
    </div>
  );
};

const AboutRecognitionsCard = () => {
  const about = React.useContext(AboutContentContext);
  return (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-[24px] p-8 md:p-10 h-full flex flex-col justify-center gap-6 transition-all duration-500 border border-gray-200 dark:border-white/10">
      <h3 className="text-xl md:text-[24px] font-bold text-[#171717] dark:text-white">Recognitions</h3>
      <p className="text-[14px] md:text-[15px] leading-[22px] md:leading-[24px] font-medium text-[#666666] dark:text-[#A3A3A3] whitespace-pre-wrap">
        {about.recognitions}
      </p>
    </div>
  );
};

const ItemContent = React.memo(({ item, isDarkMode, toggleTheme, setCurrentPage, variantImages, liveProjects, aboutImage, onPasswordRequest, onPreload }: { item: GridItemType, isDarkMode: boolean, toggleTheme: () => void, setCurrentPage: (page: string) => void, variantImages?: string[], liveProjects?: { image: string, link: string }[], aboutImage?: string, onPasswordRequest?: (projectId: string, projectTitle: string) => void, onPreload?: (id: string, navigate: boolean) => void }) => {
  switch (item.type) {
    case 'bio':
      return (
        <div className="bg-white dark:bg-[#1A1A1A] rounded-[24px] p-8 md:p-12 h-full flex flex-col justify-center gap-6 transition-all duration-500">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-[16px] md:text-[18.6px] leading-[26px] md:leading-[30px] font-medium"
          >
            Product Designer based in Saudi Arabia, from Sudan. Currently at Datalink Kuwait, where I help define the future of the product and brand.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-[16px] md:text-[18.6px] leading-[26px] md:leading-[30px] font-medium text-gray-600 dark:text-gray-400 transition-colors duration-500"
          >
            My expertise is product design, micro-animation, and anything interactive. I love building, ideating, and refining products.
          </motion.p>
        </div>
      );

    case 'email':
      return (
        <a href="mailto:munzer3a6i@gmail.com" className="bg-white dark:bg-[#1A1A1A] rounded-[24px] h-full relative flex flex-col items-center justify-center hover:scale-[1.02] transition-all duration-500 cursor-pointer">
          <div className="absolute top-6 bg-[#F3F2F2] dark:bg-[#2A2A2A] rounded-full px-4 md:px-6 py-2 md:py-2.5 text-[12px] md:text-[14.2px] font-medium transition-colors duration-500">
            Email me
          </div>
          <div className="w-[70px] h-[70px] md:w-[89px] md:h-[89px] border-[6px] md:border-[7px] border-black dark:border-white rounded-full flex items-center justify-center transition-colors duration-500">
            <div
              className="w-8 h-8 md:w-10 md:h-10 transition-colors duration-500"
              style={{
                WebkitMaskImage: "url('/envelope.svg')",
                maskImage: "url('/envelope.svg')",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                backgroundColor: "currentColor"
              }}
            />
          </div>
        </a>
      );
    case 'project':
      return (
        <ProjectCard
          id={item.id}
          title={item.title!}
          subtitle={item.subtitle!}
          description={item.description}
          image={item.image!}
          video={item.video}
          link={item.link}
          size={item.size}
          password={item.password}
          isExternalOnly={item.isExternalOnly}
          setCurrentPage={setCurrentPage}
          onPasswordRequest={onPasswordRequest}
          onPreload={onPreload}
        />
      );
    case 'theme-toggle':
      return <ThemeToggleCard isDarkMode={isDarkMode} toggleTheme={toggleTheme} />;
    case 'variant1':
      return <VariantCard images={variantImages} />;
    case 'live-projects':
      return <LiveProjectsCard isDarkMode={isDarkMode} projects={liveProjects} />;
    case 'clients':
      return <ClientsCard />;
    case 'about':
      return <AboutCard setCurrentPage={setCurrentPage} image={aboutImage} />;
    case 'about-bio':
      return <AboutBioCard />;
    case 'about-experience':
      return <AboutExperienceCard />;
    case 'about-education':
      return <AboutEducationCard />;
    case 'about-skills':
      return <AboutSkillsCard />;
    case 'about-courses':
      return <AboutCoursesCard />;
    case 'about-recognitions':
      return <AboutRecognitionsCard />;
    case 'about-email':
      return <AboutEmailCard />;
    default:
      return null;
  }
});

const SortableItem: React.FC<{ id: string, item: GridItemType, isDarkMode: boolean, toggleTheme: () => void, setCurrentPage: (page: string) => void, variantImages?: string[], liveProjects?: { image: string, link: string }[], aboutImage?: string, onPasswordRequest?: (projectId: string, projectTitle: string) => void, onPreload?: (id: string, navigate: boolean) => void }> = React.memo(({ id, item, isDarkMode, toggleTheme, setCurrentPage, variantImages, liveProjects, aboutImage, onPasswordRequest, onPreload }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 250ms cubic-bezier(0.25, 1, 0.5, 1)',
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "cursor-draggable relative group md:cursor-grab md:active:cursor-grabbing w-full",
        item.size === 'small' ? "col-span-1 md:col-span-1 lg:col-span-2 row-span-1" :
          item.size === 'medium' ? "col-span-1 md:col-span-1 lg:col-span-2 row-span-2" :
            item.size === 'wide' ? "col-span-1 md:col-span-1 lg:col-span-2 row-span-1" :
              item.size === 'large' ? "col-span-1 md:col-span-2 lg:col-span-4 row-span-2" :
                (item.id === 'drag' || item.id === 'email' || item.id === 'theme-toggle') ? "col-span-1 row-span-1" :
                  // Fallback to legacy rowSpan/colSpan if size string isn't used
                  `${item.rowSpan === 2 ? 'row-span-2' : 'row-span-1'} ${item.colSpan === 2 ? 'col-span-1 md:col-span-2 lg:col-span-4' : 'col-span-1 md:col-span-1 lg:col-span-2'}`
      )}
    >
      <motion.div
        className="w-full h-full"
        variants={{
          hidden: { opacity: 0, y: 20 },
          visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 1, 0.5, 1] } }
        }}
      >
        <ItemContent item={item} isDarkMode={isDarkMode} toggleTheme={toggleTheme} setCurrentPage={setCurrentPage} variantImages={variantImages} liveProjects={liveProjects} aboutImage={aboutImage} onPasswordRequest={onPasswordRequest} onPreload={onPreload} />
      </motion.div>
    </div>
  );
});

const MENU_ITEMS: MenuBarItem[] = [
  { icon: Home, label: "Home" },
  { icon: Palette, label: "Designs" },
  { icon: User, label: "About" },

];

const PROJECTS_DATA = [
  { id: 'mwaeedy', title: 'Mwaeedy', size: 'wide', bg: 'light' },
  { id: 'taheed', title: 'Taheed', size: 'wide', bg: 'light' },
  { id: 'weather', title: 'Weather', size: 'wide', bg: 'dark' },
  { id: 'twitter', title: 'Twitter', size: 'narrow', bg: 'light' },
  { id: 'nvb', title: 'Nvb', size: 'narrow', bg: 'light' },
  { id: 'mbok', title: 'Mbok', size: 'narrow', bg: 'light', image: '/Mbok logo.png', templateLogo: '/Mbok logo.png' },
  { id: 'calendar', title: 'Calendar', size: 'wide', bg: 'dark' },
  { id: 'interaction', title: 'Interaction Coffee', size: 'narrow', bg: 'light' },
  { id: 'datalink', title: 'Datalink', size: 'wide', bg: 'light' },
  { id: 'bakery', title: 'Bakery', size: 'wide', bg: 'light' },
  { id: 'dogwalker', title: 'Dog walker', size: 'narrow', bg: 'light' },
];

const ProjectItemContent = React.memo(({ project }: { project: any }) => {
  const content = (
    <div
      className={cn(
        "rounded-[16px] relative overflow-hidden h-full w-full transition-all duration-500 dark:hover:shadow-lg group",
        project.bg === 'dark'
          ? "bg-[#131111] shadow-[10px_10px_12px_rgba(0,0,0,0.25)]"
          : "bg-white dark:bg-[#1A1A1A] dark:shadow-[10px_10px_12px_rgba(0,0,0,0.25)]"
      )}
    >
      <div
        className="absolute inset-0 overflow-hidden"
        style={{
          ...(project.hasPadding ? { padding: '32px', backgroundColor: project.paddingColor || '#F3F4F6' } : {}),
          ...(!project.hasPadding && !project.video && !project.image ? { backgroundColor: 'var(--tw-colors-gray-100, #f3f4f6)' } : {})
        }}
      >
        {project.video ? (
          <motion.video
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            src={project.video}
            poster={project.image || undefined}
            autoPlay
            loop
            muted
            playsInline
            className={cn(
              "w-full h-full transition-transform duration-500 group-hover:scale-105",
              project.hasPadding ? "object-contain" : "object-cover"
            )}
          />
        ) : (
          <motion.img
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            src={project.image || `https://picsum.photos/seed/${project.id}/800/600`}
            alt={project.title || 'Project'}
            loading="lazy"
            decoding="async"
            className={cn(
              "w-full h-full transition-transform duration-500 group-hover:scale-105",
              project.hasPadding ? "object-contain" : "object-cover"
            )}
          />
        )}
      </div>
      {project.title && (
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <span className="text-white font-medium text-lg tracking-wide">{project.title}</span>
        </div>
      )}
    </div>
  );

  return content;
});


const SortableProjectItem: React.FC<{ id: string, project: any, onPreload?: (id: string, navigate: boolean) => void }> = React.memo(({ id, project, onPreload }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 250ms cubic-bezier(0.25, 1, 0.5, 1)',
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "relative md:cursor-grab md:active:cursor-grabbing aspect-[4/3] md:aspect-auto",
        project.size === 'small' ? "col-span-1 row-span-1" :
          project.size === 'medium' ? "col-span-1 row-span-2" :
            project.size === 'wide' ? "col-span-1 md:col-span-2 row-span-1" :
              project.size === 'large' ? "col-span-1 md:col-span-2 row-span-2" :
                project.size === 'narrow' ? "col-span-1 row-span-1" : // fallback for old narrow
                  "col-span-1 row-span-1"
      )}
    >
      <div className="w-full h-full">
        <ProjectItemContent project={project} />
      </div>
    </div>
  );
});

const ProjectsPage = ({
  isDarkMode,
  projects,
  setProjects,
  sensors,
  dropAnimation,
  onPreload
}: {
  isDarkMode: boolean,
  projects: typeof PROJECTS_DATA,
  setProjects: any,
  sensors: any,
  dropAnimation: any,
  onPreload?: (id: string, navigate: boolean) => void
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeProject = projects.find(p => p.id === activeId);

  const handleDragStart = (event: any) => {
    uiAudio.playDragStart();
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: any) => {
    uiAudio.playDrop();
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setProjects((items: any) => {
        const oldIndex = items.findIndex((item: any) => item.id === active.id);
        const newIndex = items.findIndex((item: any) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
    setActiveId(null);
  };

  return (
    <div className="max-w-[1360px] mx-auto px-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={projects.map(p => p.id)}
          strategy={rectSortingStrategy}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 auto-rows-auto md:auto-rows-[420px] grid-flow-dense">
            {projects.map((project) => (
              <SortableProjectItem key={project.id} id={project.id} project={project} onPreload={onPreload} />
            ))}
          </div>
        </SortableContext>

        {typeof document !== 'undefined' && createPortal(
          <DragOverlay dropAnimation={dropAnimation} pixelPrecision={true}>
            {activeProject ? (
              <div className="w-full h-full">
                <div className="h-full w-full shadow-[0_30px_90px_rgba(0,0,0,0.5)] rounded-[32px] cursor-grabbing scale-[1.05] rotate-2 ring-2 ring-white/20 origin-center transition-transform duration-200">
                  <ProjectItemContent project={activeProject} />
                </div>
              </div>
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </div>
  );
};

const HeroSection = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const text = "Designing the future of digital products.";
  const words = text.split(" ");

  return (
    <div className="pt-[150px] md:pt-[220px] pb-[80px] md:pb-[120px] flex flex-col gap-8 items-center justify-center text-center px-4 h-fit">
      {/* Identity Pill */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-full px-5 py-2 flex items-center gap-2"
      >
        <div className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full animate-pulse" />
        <span className="text-sm font-medium text-[#141414]/70 dark:text-white/70">Elmunzer Abdelkarim — Product Designer</span>
      </motion.div>

      <h1 className="text-[50px] md:text-[90px] leading-[1.1] md:leading-[1.1] font-bold tracking-tight max-w-[1000px] flex flex-wrap justify-center gap-x-[12px] md:gap-x-[24px] gap-y-2 md:gap-y-4">
        {words.map((word, i) => (
          <span
            key={i}
            className="inline-block cursor-default transition-transform duration-300 ease-out hover:-translate-y-2 hover:scale-105"
          >
            {word}
          </span>
        ))}
      </h1>
      <a
        href="mailto:munzer3a6i@gmail.com?subject=Get%20in%20touch"
        className="bg-[#141414] dark:bg-white text-white dark:text-[#141414] rounded-full h-[48px] px-8 font-medium hover:scale-105 transition-transform duration-300 text-sm sm:text-base shadow-lg flex items-center gap-2.5"
      >
        <Mail size={18} />
        Get in Touch
      </a>
    </div>
  );
};

const AboutHeader = ({ isDarkMode }: { isDarkMode: boolean }) => {
  return (
    <div className="flex flex-col gap-10 mb-20">
      <div className="flex flex-col md:flex-row items-center md:items-end justify-between gap-10">
        <div className="flex flex-col gap-6 max-w-[850px] text-center md:text-left">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center justify-center md:justify-start gap-4"
          >

          </motion.div>
          <motion.h1
            className="text-[56px] md:text-[96px] leading-[0.95] font-bold tracking-[-0.04em]"
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.05 } }
            }}
          >
            {"Crafting digital".split(" ").map((word, i) => (
              <motion.span
                key={i}
                className="inline-block mr-[0.2em]"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
                }}
              >
                {word}
              </motion.span>
            ))}
            <br />
            <motion.span
              className="text-[#888888] dark:text-[#333333]"
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] } }
              }}
            >
              experiences with soul.
            </motion.span>
          </motion.h1>
        </div>
      </div>

      {/* Tech Stack Pills */}

    </div>
  );
};

const SocialFooter = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const socials = [
    { name: 'X', src: '/X.png', angle: -3, link: 'https://x.com/AttieMunzer' },
    { name: 'Threads', src: '/Threads.png', angle: 3, link: 'https://www.threads.com/@munzer_3a6i' },
    { name: 'Linkedin', src: '/Linkedin.png', angle: -3, link: 'https://www.linkedin.com/in/elmunzerabdelati/' },
    { name: 'Instagram', src: '/Instagram.png', angle: -3, link: 'https://www.instagram.com/munzer_3a6i?igsh=MTJncXIzdmgzcTQ5MQ==' }
  ];

  return (
    <div className="w-full mt-5 bg-white dark:bg-[#1A1A1A] rounded-[24px] px-5 py-6 md:py-0 md:h-[92px] flex flex-col md:flex-row justify-between items-center md:items-center transition-colors duration-500 relative cursor-pointer dark:shadow-none overflow-hidden sm:overflow-visible border border-gray-200 dark:border-white/10">
      <div className="flex items-center gap-3 w-full md:w-auto z-10 relative">
        <img
          src={isDarkMode ? "/email button white.png" : "/Email Button.png"}
          alt="Email Icon"
          className="w-[26px] h-[26px] shrink-0 object-contain"
        />
        <span className="text-[20px] font-family-[SF Pro Display,sans-serif] font-medium text-[#171717] dark:text-white leading-[24px]">
          munzer3a6i@gmail.com
        </span>
      </div>

      <div className="mt-8 md:mt-0 flex justify-center md:justify-end w-full md:w-auto">
        <div className="flex items-center gap-2 sm:gap-3 md:hidden">
          {/* Mobile Flex Layout */}
          {socials.map((social, i) => (
            <a
              key={`mobile-${i}`}
              href={social.link}
              target="_blank"
              rel="noopener noreferrer"
              className="relative w-[56px] h-[56px] sm:w-[63.36px] sm:h-[63.36px] group/item"
              style={{
                top: i === 3 ? '0px' : '2.5px',
              }}
            >
              <div
                className="w-full h-full flex items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover/item:-translate-y-[15px]"
                style={{
                  rotate: `${social.angle}deg`
                }}
              >
                <img src={social.src} alt="" className="w-full h-full object-contain opacity-80 group-hover/item:opacity-100 transition-opacity duration-300" />
              </div>
              <span
                className="absolute text-center whitespace-nowrap text-[12px] leading-[14px] font-medium font-family-[SF Pro Display,sans-serif] text-black dark:text-white opacity-0 transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover/item:opacity-100 group-hover/item:-translate-y-[4px]"
                style={{
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: '8px',
                  pointerEvents: 'none'
                }}
              >
                {social.name}
              </span>
            </a>
          ))}
        </div>

        <div className="hidden md:block relative w-[283px] h-[72px]">
          {/* Desktop Absolute Layout */}
          {socials.map((social, i) => (
            <a
              key={`desktop-${i}`}
              href={social.link}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute w-[63.36px] h-full group/item"
              style={{
                left: `${i * 70.6}px`,
                top: i === 3 ? '0px' : '2.5px',
              }}
            >
              <div
                className="w-[63.36px] h-[63.36px] flex items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover/item:-translate-y-[30px]"
                style={{
                  rotate: `${social.angle}deg`
                }}
              >
                <img src={social.src} alt="" className="w-full h-full object-contain opacity-80 group-hover/item:opacity-100 group-hover/item:drop-shadow-[0_4px_12px_rgba(255,255,255,0.15)] transition-all duration-300" />
              </div>
              <span
                className="absolute text-center whitespace-nowrap text-[12px] leading-[14px] font-medium font-family-[SF Pro Display,sans-serif] text-black dark:text-white opacity-0 transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover/item:opacity-100 group-hover/item:-translate-y-[12px]"
                style={{
                  top: '52px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  pointerEvents: 'none'
                }}
              >
                {social.name}
              </span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

const ProjectDetailPage = ({ projectId, isDarkMode, projects, setCurrentPage, onPasswordRequest, imageCache }: { projectId: string; isDarkMode: boolean, projects: GridItemType[], setCurrentPage: (page: string) => void, onPasswordRequest?: (projectId: string, projectTitle: string) => void, imageCache: Record<string, string> }) => {
  const [loadedImages, setLoadedImages] = React.useState<Record<string, string>>(imageCache || {});

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [projectId]);

  const project = projects.find(p => p.id === projectId);

  React.useEffect(() => {
    if (!project) return;
    // Supabase doesn't use subcollections. Images should already be in templateLayoutImages.
    // If they are missing, we might need to handle it differently, but for now we just skip fetching from Firebase.
  }, [project, imageCache, loadedImages]);

  // Sync local state if global cache updates
  React.useEffect(() => {
    if (Object.keys(imageCache).length > 0) {
      setLoadedImages(prev => ({ ...prev, ...imageCache }));
    }
  }, [imageCache]);

  if (!project) return <div className="p-24 text-center font-sans tracking-tight">Project not found</div>;

  const validProjects = projects.filter(p => p.type === 'project' && !p.isExternalOnly && p.page === 'home');
  const currentIndex = validProjects.findIndex(p => p.id === projectId);

  const prevProject = currentIndex > 0 ? validProjects[currentIndex - 1] : validProjects[validProjects.length - 1];
  const nextProject = currentIndex >= 0 && currentIndex < validProjects.length - 1 ? validProjects[currentIndex + 1] : validProjects[0];

  return (
    <div className="w-full max-w-[1320px] px-4 mx-auto flex flex-col gap-5 md:gap-8 pb-16 md:pb-24 mt-[120px] md:mt-[200px] group/page transition-all duration-500 h-fit">

      {/* Back Button */}
      <button
        onClick={() => setCurrentPage('Home')}
        className="flex items-center gap-2 text-[#717171] hover:text-black dark:hover:text-white transition-colors duration-300 group/back self-start -mt-4 mb-2"
      >
        <ArrowLeft size={18} className="transition-transform duration-300 group-hover/back:-translate-x-1" />
        <span className="text-sm font-medium">Back to Home</span>
      </button>

      {/* Header and Info Container */}
      <div className="w-full flex flex-col gap-8">
        {/* Top Header Frame */}
        <div className="w-full flex flex-col gap-8">
          {/* Logo & Pill */}
          <div className="w-full flex justify-between items-center h-[82.94px]">
            {project.templateLogo ? (
              <img src={project.templateLogo} alt="Project Logo" className="h-[82.94px] object-contain mx-auto md:mx-0 w-auto max-w-[157px]" fetchPriority="high" />
            ) : <div className="w-[157px]" />}
          </div>

          {/* Headings */}
          <div className="w-full flex flex-col gap-2 mt-4 text-center md:text-left self-center md:self-start">
            {project.templateHeading && (
              <h1 className="font-sans text-[28px] leading-[32px] tracking-tight text-black dark:text-white">
                {project.templateHeading}
              </h1>
            )}
            {project.templateSubHeading && (
              <p className="font-sans text-[18px] leading-[22px] tracking-tight text-[#717171]">
                {project.templateSubHeading}
              </p>
            )}
            {/* Action Buttons: Visit Website and/or View Case Study */}
            <div className="mt-4 flex flex-wrap items-center justify-center md:justify-start gap-3">
              {project.link && (
                <a href={project.link.startsWith('http') ? project.link : `https://${project.link}`} target="_blank" rel="noopener noreferrer">
                  <span className="px-5 py-3 bg-[#1A1A1A] text-white dark:bg-white dark:text-black font-medium text-sm rounded-full tracking-tight hover:opacity-80 transition-opacity inline-flex items-center gap-2">
                    <ExternalLink size={14} />
                    Visit website
                  </span>
                </a>
              )}
              {project.caseStudyLink && (
                <a href={project.caseStudyLink.startsWith('http') ? project.caseStudyLink : `https://${project.caseStudyLink}`} target="_blank" rel="noopener noreferrer">
                  <span className={`px-5 py-3 font-medium text-sm rounded-full tracking-tight hover:opacity-80 transition-opacity inline-flex items-center gap-2 ${project.link ? 'bg-transparent border border-[#1A1A1A] text-[#1A1A1A] dark:border-white dark:text-white' : 'bg-[#1A1A1A] text-white dark:bg-white dark:text-black'}`}>
                    View case study
                  </span>
                </a>
              )}
              {project.templateLink && !project.caseStudyLink && (
                <a href={project.templateLink.startsWith('http') ? project.templateLink : `https://${project.templateLink}`} target="_blank" rel="noopener noreferrer">
                  <span className={`px-5 py-3 font-medium text-sm rounded-full tracking-tight hover:opacity-80 transition-opacity inline-flex items-center gap-2 ${project.link ? 'bg-transparent border border-[#1A1A1A] text-[#1A1A1A] dark:border-white dark:text-white' : 'bg-[#1A1A1A] text-white dark:bg-white dark:text-black'}`}>
                    View case study
                  </span>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Body & Responsibilities */}
        <div className="w-full flex flex-col md:flex-row gap-8">
          {/* Box 1 */}
          <div className="flex-1 bg-white dark:bg-[#1A1A1A] border border-[#EAEAEA] dark:border-white/10 rounded-[12px] p-5 flex flex-col gap-3 min-h-[136px] border-l-4 border-l-[#141414] dark:border-l-white/30">
            <div className="flex items-center gap-2.5">
              <Briefcase size={18} className="text-[#717171] shrink-0" />
              <h2 className="font-sans text-[24px] leading-[32px] tracking-tight text-black dark:text-white">Responsibilities</h2>
            </div>
            <p className="font-sans text-[18px] leading-[24px] tracking-tight text-[#717171] whitespace-pre-wrap">{project.templateBody || project.description}</p>
          </div>
          {/* Box 2 */}
          <div className="flex-1 bg-white dark:bg-[#1A1A1A] border border-[#EAEAEA] dark:border-white/10 rounded-[12px] p-5 flex flex-col gap-3 min-h-[136px] border-l-4 border-l-[#141414] dark:border-l-white/30">
            <div className="flex items-center gap-2.5">
              <Wrench size={18} className="text-[#717171] shrink-0" />
              <h2 className="font-sans text-[24px] leading-[32px] tracking-tight text-black dark:text-white">Tools & Tech</h2>
            </div>
            <p className="font-sans text-[18px] leading-[24px] tracking-tight text-[#717171] whitespace-pre-wrap">{project.templateResponsibilities || ''}</p>
          </div>
        </div>
      </div>

      {/* Images Area */}
      {((project.templateLayoutImages && project.templateLayoutImages.length > 0) || (project.templateImages && project.templateImages.length > 0)) && (
        <div className="w-full flex justify-center items-center">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 w-full">
            {(project.templateLayoutImages || project.templateImages?.map((url, i) => ({ id: `old_${i}`, url, size: i % 3 === 0 ? 'large' : 'small' })) || []).map((imgObj: any, i: number) => {
              const isFull = imgObj.size === 'large';
              const sourceUrl = imgObj.hasSubcollectionData ? (loadedImages[imgObj.id] || imageCache[imgObj.id]) : imgObj.url;

              return (
                <div key={imgObj.id || i} className={`rounded-[16px] md:rounded-[24px] overflow-hidden bg-[#080D07] flex flex-col relative ${isFull ? 'col-span-1 md:col-span-2' : 'col-span-1'}`}>
                  {sourceUrl ? (
                    <img src={sourceUrl} className="w-full h-auto object-contain md:object-cover md:h-full md:min-h-[300px]" loading={i < 2 ? "eager" : "lazy"} />
                  ) : (
                    <div className="flex w-full h-full min-h-[200px] md:min-h-[300px] items-center justify-center text-gray-500 animate-pulse">Loading image...</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Project Counter & Prev/Next */}
      {currentIndex >= 0 && (
        <div className="flex justify-center">
          <span className="text-sm text-[#717171] font-medium bg-white/5 dark:bg-white/5 border border-[#EAEAEA] dark:border-white/10 rounded-full px-4 py-1.5">
            {currentIndex + 1} of {validProjects.length} designs
          </span>
        </div>
      )}
      {currentIndex !== -1 && (
        <div className="w-full flex flex-col md:flex-row gap-4 md:gap-8">
          {prevProject && (
            <div
              onClick={() => {
                if (prevProject.password && onPasswordRequest) {
                  onPasswordRequest(prevProject.id, prevProject.title || '');
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setCurrentPage(`Project:${prevProject.id}`);
                }
              }}
              className="w-full md:flex-1 shrink-0 h-[140px] md:h-[248px] bg-[#121111] rounded-[20px] md:rounded-[24px] relative overflow-hidden cursor-pointer group"
            >
              <div className="absolute inset-0">
                {prevProject.video ? (
                  <video src={prevProject.video} poster={prevProject.image || undefined} autoPlay loop muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-40 transition-opacity duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] scale-100 group-hover:scale-105" />
                ) : prevProject.image && <img src={prevProject.image} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-40 transition-opacity duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] scale-100 group-hover:scale-105" />}
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
              </div>
              <div className="absolute top-5 left-6 md:top-8 md:left-8 text-[#BCBCBC] text-[18px] md:text-[28px] font-sans transition-transform duration-500 group-hover:-translate-y-1">Previous</div>
              <div className="absolute bottom-5 left-6 md:bottom-8 md:left-8 flex items-center gap-2 md:gap-3 w-[calc(100%-48px)]">
                <span className="text-white text-[24px] md:text-[42px] font-sans font-medium transition-transform duration-500 group-hover:translate-x-2 truncate">{prevProject.title}</span>
                {prevProject.password && <Lock size={18} className="text-white/70 shrink-0 md:w-5 md:h-5" />}
              </div>
            </div>
          )}

          {nextProject && (
            <div
              onClick={() => {
                if (nextProject.password && onPasswordRequest) {
                  onPasswordRequest(nextProject.id, nextProject.title || '');
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setCurrentPage(`Project:${nextProject.id}`);
                }
              }}
              className="w-full md:flex-1 shrink-0 h-[140px] md:h-[248px] bg-[#121111] rounded-[20px] md:rounded-[24px] relative overflow-hidden cursor-pointer group"
            >
              <div className="absolute inset-0">
                {nextProject.video ? (
                  <video src={nextProject.video} poster={nextProject.image || undefined} autoPlay loop muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-40 transition-opacity duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] scale-100 group-hover:scale-105" />
                ) : nextProject.image && <img src={nextProject.image} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-40 transition-opacity duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] scale-100 group-hover:scale-105" />}
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
              </div>
              <div className="absolute top-5 left-6 md:top-8 md:left-8 text-[#BCBCBC] text-[18px] md:text-[28px] font-sans transition-transform duration-500 group-hover:-translate-y-1">Next</div>
              <div className="absolute bottom-5 left-6 md:bottom-8 md:left-8 flex items-center gap-2 md:gap-3 w-[calc(100%-48px)]">
                <span className="text-white text-[24px] md:text-[42px] font-sans font-medium transition-transform duration-500 group-hover:translate-x-2 truncate">{nextProject.title}</span>
                {nextProject.password && <Lock size={18} className="text-white/70 shrink-0 md:w-5 md:h-5" />}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const BackgroundMesh = ({ isDarkMode }: { isDarkMode: boolean }) => null;

export default function App() {
  const [items, setItems] = useState<GridItemType[]>(INITIAL_ITEMS);
  const [aboutItems, setAboutItems] = useState<GridItemType[]>([]);
  const [projectItems, setProjectItems] = useState<GridItemType[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [currentPage, setCurrentPageRaw] = useState('Home');
  const [isLoading, setIsLoading] = useState(false);
  const [variantImages, setVariantImages] = useState<string[]>([]);
  const [liveProjects, setLiveProjects] = useState<{ image: string, link: string }[]>([]);
  const [homeLayout, setHomeLayout] = useState<string[] | null>(null);
  const [homeProjectsData, setHomeProjectsData] = useState<any[]>([]);
  const [allProjects, setAllProjects] = useState<GridItemType[]>([]);
  const [projectImagesCache, setProjectImagesCache] = useState<Record<string, Record<string, string>>>({});
  const [isNavigating, setIsNavigating] = useState(false);

  const preloadProject = useCallback(async (id: string, navigate: boolean = false) => {
    // If we're already navigating, don't trigger again
    if (isNavigating && !navigate) return;

    const project = allProjects.find(p => p.id === id);
    if (!project) return;

    // Check if we already have the subcollection images
    const hasCache = !!projectImagesCache[id];

    if (navigate) {
      if (hasCache) {
        setCurrentPageRaw(`Project:${id}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      setIsNavigating(true);
    }

    try {
      let urls: Record<string, string> = projectImagesCache[id] || {};

      if (!hasCache) {
        setProjectImagesCache(prev => ({ ...prev, [id]: urls }));
      }

      // Pre-load all critical images
      const imagesToLoad = [
        project.image,
        project.templateLogo,
        ...Object.values(urls),
        ...(project.templateImages || [])
      ].filter(Boolean) as string[];

      await Promise.all(imagesToLoad.map(url => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = url;
          img.onload = resolve;
          img.onerror = resolve;
        });
      }));

      if (navigate) {
        setCurrentPageRaw(`Project:${id}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (e) {
      if (navigate) {
        setCurrentPageRaw(`Project:${id}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      if (navigate) setIsNavigating(false);
    }
  }, [allProjects, projectImagesCache, isNavigating]);

  // Item #1: Scroll to top on every page transition
  const setCurrentPage = useCallback((page: string) => {
    setCurrentPageRaw(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  const [aboutImage, setAboutImage] = useState<string>('');
  const [aboutContent, setAboutContent] = useState<AboutContent>(DEFAULT_ABOUT);

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Play a click sound for buttons, links, or generally interactive elements
      if (
        target.closest('button') ||
        target.closest('a') ||
        target.closest('[role="button"]') ||
        target.closest('.cursor-pointer')
      ) {
        uiAudio.playClick();
      }
    };
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  React.useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [projectsRes, settingsRes] = await Promise.all([
          supabase.from('projects').select('id,data').limit(50),
          supabase.from('settings').select('id,data').limit(20)
        ]);
        
        if (projectsRes.data) {
          const projectsData = projectsRes.data.map((d: any) => ({ id: d.id, ...d.data }));
          
          setAllProjects(projectsData.map((p: any) => ({
            id: p.id,
            type: 'project',
            ...p
          })));

          const homeProjects = projectsData.filter((p: any) => p.page === 'home').sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
          const projectsPageProjects = projectsData.filter((p: any) => p.page === 'projects').sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

          setHomeProjectsData(homeProjects);

          setProjectItems(projectsPageProjects.map((p: any) => ({
            id: p.id,
            title: p.title,
            size: p.size,
            bg: 'light',
            image: p.image,
            video: p.video,
            link: p.link,
            hasPadding: p.hasPadding,
            paddingColor: p.paddingColor,
            isExternalOnly: p.isExternalOnly
          })));
        }
        if (settingsRes.data) {
          settingsRes.data.forEach((setting: any) => {
            if (setting.id === 'aboutMe') {
              const data = setting.data;
              if (data.image) setAboutImage(data.image);
              setAboutContent(mergeAboutContent(data));
            } else if (setting.id === 'variantCard') {
              const data = setting.data;
              setVariantImages([data.image1, data.image2, data.image3, data.image4].filter(Boolean));
            } else if (setting.id === 'liveProject') {
              setLiveProjects(setting.data.projects || []);
            } else if (setting.id === 'homeLayout') {
              setHomeLayout(setting.data.items || []);
            }
          });
          
          setAboutItems(ABOUT_ITEMS);
        } else {
          setAboutItems(ABOUT_ITEMS);
          setHomeLayout([]);
        }
        
      } catch (err) {
        setAboutItems(ABOUT_ITEMS);
        setHomeLayout([]);
      }
    };

    fetchAllData();
  }, []);

  React.useEffect(() => {
    const staticItems = INITIAL_ITEMS.filter(item => item.type !== 'project');
    const dynamicProjects = (homeProjectsData.length > 0 ? homeProjectsData : INITIAL_ITEMS.filter(item => item.type === 'project')).map(p => ({
      id: p.id,
      type: 'project',
      size: p.size,
      rowSpan: p.size === 'large' ? 2 : 1,
      colSpan: p.size === 'large' ? 2 : 1,
      title: p.title,
      subtitle: p.subtitle,
      description: p.description,
      image: p.image,
      video: p.video,
      link: p.link,
      password: p.password,
      templateLogo: p.templateLogo,
      templateHeaderPill: p.templateHeaderPill,
      templateHeading: p.templateHeading,
      templateSubHeading: p.templateSubHeading,
      templateBody: p.templateBody,
      templateResponsibilities: p.templateResponsibilities,
      templateImages: p.templateImages,
      templateLayoutImages: p.templateLayoutImages,
      templateLink: p.templateLink,
      caseStudyLink: p.caseStudyLink,
      isExternalOnly: p.isExternalOnly,
    }));

    const availableItems = [...staticItems, ...dynamicProjects];

    const sortedItems = [...availableItems].sort((a, b) => {
      const indexA = (homeLayout || []).indexOf(a.id);
      const indexB = (homeLayout || []).indexOf(b.id);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    }).filter(Boolean) as GridItemType[];

    setItems(sortedItems);

  }, [homeLayout, homeProjectsData, allProjects, variantImages, liveProjects, aboutImage]);

  const toggleTheme = () => setIsDarkMode(!isDarkMode);

  // Password protection state
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordTargetProject, setPasswordTargetProject] = useState<{ id: string, title: string } | null>(null);
  const [passwordError, setPasswordError] = useState('');

  const onPasswordRequest = useCallback((projectId: string, projectTitle: string) => {
    setPasswordTargetProject({ id: projectId, title: projectTitle });
    setPasswordError('');
    setPasswordModalOpen(true);
  }, []);

  const handlePasswordSubmit = useCallback((enteredPassword: string) => {
    if (!passwordTargetProject) return;
    const project = items.find(p => p.id === passwordTargetProject.id);
    if (project && project.password && enteredPassword === project.password) {
      setPasswordModalOpen(false);
      setPasswordTargetProject(null);
      setPasswordError('');
      setCurrentPage(`Project:${passwordTargetProject.id}`);
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  }, [passwordTargetProject, items]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: any) => {
    uiAudio.playDragStart();
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: any) => {
    uiAudio.playDrop();
    const { active, over } = event;

    if (over && active.id !== over.id) {
      if (currentPage === 'Home') {
        setItems((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          return arrayMove(items, oldIndex, newIndex);
        });
      } else if (currentPage === 'About') {
        setAboutItems((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          return arrayMove(items, oldIndex, newIndex);
        });
      }
    }
    setActiveId(null);
  };

  const activeItem = currentPage === 'Home'
    ? items.find(item => item.id === activeId)
    : currentPage === 'About'
      ? aboutItems.find(item => item.id === activeId)
      : null;

  const dropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.4',
        },
      },
    }),
    duration: 300,
    easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  };

  return (
    <AboutContentContext.Provider value={aboutContent}>
    <ReactLenis root>
      <CustomCursor />
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#1C1C1C] overflow-hidden"
          >
            <div className="flex flex-col items-center justify-center relative w-full max-w-[1440px] h-full min-h-[600px]">

              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="absolute top-[386px] text-white text-[32px] leading-[32px] font-normal text-center w-full max-w-[643px] px-4 -translate-y-1/2"
                style={{ fontFamily: "'SF Pro Display', sans-serif" }}
              >
                Please wait while we get our ducks in a row.
              </motion.h1>

              {/* Duck */}
              <motion.div
                className="absolute z-10 w-[100px] h-[91.5px] -translate-x-1/2"
                style={{ top: '546px', left: '50%' }}
                animate={{ y: [0, -126, 0, -126, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  times: [0, 0.25, 0.5, 0.75, 1]
                }}
              >
                <img src="/duck.svg" alt="Duck" className="w-full h-full object-contain" />
              </motion.div>

              {/* Wave */}
              <div
                className="absolute top-[575px] h-[129px] -translate-y-1/2 pointer-events-none"
                style={{ width: '4236px', left: 'calc(50% - 2103px)' }}
              >
                <motion.div
                  animate={{ x: [0, -706] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  className="flex h-full w-full"
                >
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-[706px] h-full flex-shrink-0 relative overflow-hidden">
                      <img src="/wiggly line.svg" alt="" className="absolute top-0 left-0 min-w-[1036px] h-[129px] object-cover object-left max-w-none" />
                    </div>
                  ))}
                </motion.div>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={cn("min-h-screen font-sans pb-24 overflow-x-hidden transition-colors duration-500 relative", isDarkMode ? "dark bg-[#0A0A0A] text-white" : "bg-[#F6F5F1] text-[#171717]")}>
        <TopLoadingBar isVisible={isNavigating} />
        <BackgroundMesh isDarkMode={isDarkMode} />

        {/* Main Content Area */}
        <main>
        <AnimatePresence mode="wait">
          {currentPage === 'Home' || currentPage === 'Designs' ? (
            <motion.div
              key={currentPage}
              initial="hidden"
              animate={!isLoading ? "visible" : "hidden"}
              exit="exit"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.08, delayChildren: 0.3 }
                },
                exit: { opacity: 0, transition: { duration: 0.3 } }
              }}
              className="flex flex-col w-full"
            >
              {/* Hero */}
              <motion.div variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
              }}>
                <HeroSection isDarkMode={isDarkMode} />
              </motion.div>

              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
                }}
                className="max-w-[1320px] mx-auto px-4 w-full"
              >
                {currentPage === 'Home' ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCorners}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={items.map(i => i.id)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 auto-rows-[327px] grid-flow-dense">
                        {items.map((item) => (
                          <SortableItem key={item.id} id={item.id} item={item} isDarkMode={isDarkMode} toggleTheme={toggleTheme} setCurrentPage={setCurrentPage} variantImages={variantImages} liveProjects={liveProjects} aboutImage={aboutImage} onPasswordRequest={onPasswordRequest} onPreload={preloadProject} />
                        ))}
                      </div>
                    </SortableContext>

                    {typeof document !== 'undefined' && createPortal(
                      <DragOverlay dropAnimation={dropAnimation} pixelPrecision={true}>
                        {activeItem ? (
                          <div className="w-full h-full">
                            <div className="h-full w-full shadow-[0_30px_90px_rgba(0,0,0,0.5)] rounded-[32px] cursor-grabbing scale-[1.05] rotate-2 ring-2 ring-white/20 origin-center transition-transform duration-200">
                              <ItemContent item={activeItem} isDarkMode={isDarkMode} toggleTheme={toggleTheme} setCurrentPage={setCurrentPage} variantImages={variantImages} liveProjects={liveProjects} aboutImage={aboutImage} onPasswordRequest={onPasswordRequest} onPreload={preloadProject} />
                            </div>
                          </div>
                        ) : null}
                      </DragOverlay>,
                      document.body
                    )}
                  </DndContext>
                ) : null}

                {currentPage === 'Designs' ? (
                  <ProjectsPage
                    isDarkMode={isDarkMode}
                    projects={projectItems}
                    setProjects={setProjectItems}
                    sensors={sensors}
                    dropAnimation={dropAnimation}
                    onPreload={preloadProject}
                  />
                ) : null}

                {currentPage === 'Home' && <SocialFooter isDarkMode={isDarkMode} />}
              </motion.div>
            </motion.div>
          ) : currentPage === 'About' ? (
            <motion.div
              key="about"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.2, delayChildren: 0.1 }
                },
                exit: { opacity: 0, transition: { duration: 0.3 } }
              }}
              className="flex flex-col w-full"
            >
              <motion.div variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
              }}>
                <HeroSection isDarkMode={isDarkMode} />
              </motion.div>

              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
                }}
                className="max-w-[1320px] mx-auto px-4 w-full"
              >

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCorners}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={aboutItems.map(i => i.id)}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 auto-rows-[327px] grid-flow-dense">
                      {aboutItems.map((item) => (
                        <SortableItem key={item.id} id={item.id} item={item} isDarkMode={isDarkMode} toggleTheme={toggleTheme} setCurrentPage={setCurrentPage} variantImages={variantImages} liveProjects={liveProjects} aboutImage={aboutImage} onPasswordRequest={onPasswordRequest} />
                      ))}
                    </div>
                  </SortableContext>

                  {typeof document !== 'undefined' && createPortal(
                    <DragOverlay dropAnimation={dropAnimation}>
                      {activeItem ? (
                        <div className="w-full h-full">
                          <div className="h-full w-full shadow-[0_30px_60px_rgba(0,0,0,0.3)] rounded-[24px] cursor-grabbing scale-[1.05] rotate-2 ring-2 ring-black/10 origin-center">
                            <ItemContent item={activeItem} isDarkMode={isDarkMode} toggleTheme={toggleTheme} setCurrentPage={setCurrentPage} variantImages={variantImages} liveProjects={liveProjects} aboutImage={aboutImage} onPasswordRequest={onPasswordRequest} />
                          </div>
                        </div>
                      ) : null}
                    </DragOverlay>,
                    document.body
                  )}
                </DndContext>
              </motion.div>
            </motion.div>
          ) : currentPage.startsWith('Project:') ? (
            <motion.div
              key={`project-detail-${currentPage}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="w-full"
            >
              <ProjectDetailPage
                projectId={currentPage.split(':')[1]}
                isDarkMode={isDarkMode}
                projects={allProjects}
                setCurrentPage={setCurrentPage}
                onPasswordRequest={onPasswordRequest}
                imageCache={projectImagesCache[currentPage.split(':')[1]] || {}}
              />
            </motion.div>

          ) : (
            <motion.div
              key="other"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center justify-center h-[400px] text-xl font-medium opacity-50"
            >
              {currentPage} Page Coming Soon
            </motion.div>
          )}
        </AnimatePresence>
        </main>

        {/* Fixed Top Navigation */}
        <motion.div
          initial={{ y: -100, opacity: 0, x: '-50%' }}
          animate={{ y: 0, opacity: 1, x: '-50%' }}
          transition={{ type: "spring", stiffness: 100, damping: 20, delay: 1 }}
          className="fixed top-6 left-1/2 z-50 w-fit"
        >
          <MenuBar
            items={MENU_ITEMS}
            activeTab={currentPage}
            onTabChange={setCurrentPage}
          />
        </motion.div>
      </div>

      {/* Password Modal */}
      <PasswordModal
        isOpen={passwordModalOpen}
        onClose={() => { setPasswordModalOpen(false); setPasswordTargetProject(null); }}
        onSubmit={handlePasswordSubmit}
        projectTitle={passwordTargetProject?.title || ''}
      />
      {/* Password error overlay */}
      {passwordModalOpen && passwordError && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[250] bg-red-500/90 text-white text-sm font-medium px-6 py-3 rounded-full backdrop-blur-md shadow-lg animate-bounce">
          {passwordError}
        </div>
      )}
      <Analytics />
    </ReactLenis>
    </AboutContentContext.Provider>
  );
}
